import Joyride, {
  CallBackProps,
  STATUS,
  Step as StepType,
} from 'react-joyride';
import { useWalkthroughContext } from '@/context/walkthrougContext';
import { getJoyrideStyles } from '@/components/walkthrough/styles';
import { Tooltip } from '@/components/walkthrough/tooltip';
import { useCallback, useMemo } from 'react';

export type ClientsWrapperProps = {
  steps: StepType[];
  type: 'single' | 'multi' | 'decline';
};

export default function ClientsWrapper({ steps, type }: ClientsWrapperProps) {
  const {
    setState,
    state: { run, stepIndex },
  } = useWalkthroughContext();

  const lastStepIndex = Math.max(steps.length - 1, 0);
  const isFinalStep = stepIndex >= lastStepIndex;
  const isDeclineTip = type === 'decline';

  const endTour = useCallback(() => {
    setState({ run: false, stepIndex: 0, tourActive: false });
  }, [setState]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, type: eventType, status } = data;

      // Only finish when Joyride reports a real terminal status.
      // Do NOT end on action:'close' / tour:end alone — those fire during
      // target-not-found / restarts and were killing the tour before the UI mounted.
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        endTour();
        return;
      }

      // Ignore target-not-found; the starter retries until the DOM is ready.
      if (eventType === 'error:target_not_found') {
        return;
      }

      if (eventType !== 'step:after') {
        return;
      }

      if (action === 'next' || action === 'close') {
        if (isDeclineTip || index >= lastStepIndex) {
          endTour();
          return;
        }
        setState({ run: true, tourActive: true, stepIndex: index + 1 });
        return;
      }

      if (action === 'prev' && index > 0) {
        setState({ run: true, tourActive: true, stepIndex: index - 1 });
      }
    },
    [endTour, isDeclineTip, lastStepIndex, setState]
  );

  const joyrideStyles = useMemo(
    () =>
      getJoyrideStyles(
        isFinalStep || isDeclineTip,
        isFinalStep || isDeclineTip
      ),
    [isDeclineTip, isFinalStep]
  );

  if (!steps.length) {
    return null;
  }

  return (
    <Joyride
      // Remount when the tour starts so Joyride re-binds to fresh DOM targets
      // (ClientList is unmounted while the info page is open).
      key={
        run ? `clients-tour-run-${type}-${steps.length}` : 'clients-tour-idle'
      }
      callback={handleCallback}
      continuous
      run={run}
      stepIndex={stepIndex}
      steps={steps}
      tooltipComponent={(tooltipProps) => {
        // Step 0 (multi): above Client type — no default side margin over the control
        const tooltipClassName =
          type === 'multi' && tooltipProps.index === 0
            ? 'ml-0 mr-2 mb-2'
            : isDeclineTip
            ? 'ml-0 mr-2 mt-2'
            : undefined;

        return (
          <Tooltip
            {...tooltipProps}
            className={tooltipClassName}
            pollyInformationalSteps={
              isDeclineTip ? [] : type === 'multi' ? [0, 1, 2, 3, 4] : [0, 1]
            }
            pollyTimeSteps={isDeclineTip ? [0] : undefined}
            pollyImpressedSteps={isDeclineTip ? [] : [lastStepIndex]}
            displayCloseButton={
              isDeclineTip ? false : tooltipProps.index < lastStepIndex
            }
          />
        );
      }}
      scrollToFirstStep
      showProgress
      showSkipButton={false}
      disableOverlayClose
      disableCloseOnEsc
      floaterProps={{
        disableAnimation: true,
        // Multi search step + decline tip: keep fixed under the target
        ...((type === 'multi' && stepIndex === 3) || isDeclineTip
          ? { disableFlip: true, placement: 'bottom' as const }
          : {}),
      }}
      styles={joyrideStyles}
    />
  );
}
